/* =====================================================================================
   Prevalent functioning grafts: what is published, and what is modelled.

   THE FAULT THIS FIXES. The Atlas showed roughly 1.17 million prevalent grafts across its 78
   countries, derived by multiplying every country's annual transplant volume by one graft-year
   multiplier. Three things were wrong with that, and they do not point the same way.

   1. THE MULTIPLIER WAS CALIBRATED AGAINST ONE DENOMINATOR AND APPLIED TO ANOTHER.
      8.44 came from dividing the USRDS figure of 273,041 functioning US kidney grafts by
      32,363, the sum of kidney and kidney-pancreas volume across the 254 US CENTRES in the
      seed. The tile then applies it to the NATIONAL figure of 28,000. Those are not the same
      number, and the mismatch understates the United States by 13 per cent: 28,000 x 8.44 is
      236,320 against a published 273,041. On the national basis the US multiplier is 9.75.

   2. ONE MULTIPLIER CANNOT SERVE EVERY COUNTRY, because it is not a property of kidneys. It is
      prevalence divided by CURRENT volume, so it measures how much a programme has grown. A
      programme transplanting at the same rate for thirty years accumulates far more living
      recipients per current transplant than one that has doubled in a decade. Every published
      anchor sits above 8.44, and they spread widely:

        United States   273,041 / 28,000 =  9.75   (USRDS, end-2024)
        United Kingdom   43,100 /  3,400 = 12.68   (NHSBT, 2024)
        Australia        13,648 /  1,000 = 13.65   (ANZDATA 47th report, end-2023)
        New Zealand       2,374 /    175 = 13.57   (ANZDATA 47th report, end-2023)

      The United States is the outlier precisely because its volume has grown fastest.

   3. IT WAS NEVER A WORLD FIGURE. 78 countries are mapped. The Global Observatory recorded
      179,091 solid organ transplants worldwide in 2023 across 194 countries; this model holds
      148,742, so roughly 83 per cent, with China and most of Africa absent.

   HOW FAR THE ANCHORS NOW REACH. Nine countries carry a published or registry-derived figure
   rather than a multiplier, covering about 38 per cent of the total: the four above, plus
   France, Italy and the Netherlands computed from the ERA Registry's own published inputs, plus
   India derived from its own volume history.

   THE EUROPEAN CROSS-CHECK. The ERA Registry reports 567,440 people on kidney replacement
   therapy in Europe at the end of 2022, of whom 39 per cent were living with a functioning
   graft: 221,302. The model reproduces that closely once Turkiye, which the registry does not
   cover, is set aside. Europe is therefore broadly sound and, on the French and Italian
   figures, was if anything slightly UNDERSTATED by the flat multiplier.

   WHAT REMAINS MODELLED, AND WHERE THE RISK SITS. Brazil, Turkiye, Iran and Mexico still carry
   a multiplier and no anchor. Their programmes are younger and their graft survival poorer than
   any anchor country, so 9.75 is an upper bound for them rather than an estimate, and their
   combined 149,000 is the least reliable block in the total. Naming that is more useful than
   quietly reducing it to a number that would look more comfortable.
   ===================================================================================== */

export interface PublishedPrevalence {
  /** Functioning grafts, as the national registry reports them. */
  value: number;
  organ: 'kidney';
  asAt: string;
  source: string;
  sourceUrl?: string;
}

/* Verified national counts. These are read INSTEAD of the arithmetic, never alongside it: a
   published census beats a model, and mixing the two produces a figure that is neither. */
export const PUBLISHED_PREVALENCE: Record<string, PublishedPrevalence> = {
  USA: {
    value: 273_041, organ: 'kidney', asAt: '2024-12-31',
    source: 'USRDS Annual Data Report: patients living with a functioning kidney transplant',
  },
  GBR: {
    value: 43_100, organ: 'kidney', asAt: '2024-12-31',
    source: 'NHS Blood and Transplant: recipients living with a functioning kidney transplant',
  },
  AUS: {
    value: 13_648, organ: 'kidney', asAt: '2023-12-31',
    source: 'ANZDATA 47th Annual Report, chapter 7: grafts still functioning',
  },
  NZL: {
    value: 2_374, organ: 'kidney', asAt: '2023-12-31',
    source: 'ANZDATA 47th Annual Report, chapter 7: grafts still functioning',
  },

  /* ---------------------------------------------------------------------------------
     DERIVED FROM A REGISTRY THAT PUBLISHES THE INPUTS BUT NOT THE ANSWER.

     The ERA Registry reports, per country, the prevalence of kidney replacement therapy per
     million population and the percentage of those patients living with a functioning graft.
     Multiplying the two by population gives the count directly. That is arithmetic on two
     published figures, not a model, so it is graded 'derived' rather than 'estimated'.
     --------------------------------------------------------------------------------- */
  FRA: {
    value: 44_461, organ: 'kidney', asAt: '2022-12-31',
    source: 'ERA Registry Annual Report 2022: 1,383 pmp on kidney replacement therapy, 47% '
          + 'living with a functioning graft, on a population of 68.4 million',
  },
  ITA: {
    value: 32_333, organ: 'kidney', asAt: '2022-12-31',
    source: 'ERA Registry Annual Report 2022: 1,165 pmp on kidney replacement therapy, 47% '
          + 'living with a functioning graft, on a population of 59.05 million',
  },
  NLD: {
    value: 8_960, organ: 'kidney', asAt: '2022-12-31',
    source: 'ERA Registry Annual Report 2022: 1,065 pmp on kidney replacement therapy, 47% '
          + 'living with a functioning graft, on a population of 17.9 million',
  },

  /* ---------------------------------------------------------------------------------
     INDIA, WHICH THE FLAT MULTIPLIER GOT IMPOSSIBLY WRONG.

     At 9.75 the model produced 141,151 people living with a functioning kidney graft in India.
     India has not performed that many transplants in its entire history. NOTTO's own series
     gives roughly 83,000 kidney transplants across 2013 to 2023 inclusive, and volumes before
     that were a fraction of current levels; cumulative activity since the programme began is on
     the order of 130,000 to 140,000. A prevalence of 141,151 would require essentially every
     Indian recipient ever transplanted to be alive today with a functioning graft.

     That is the clearest possible demonstration of why a multiplier calibrated on the United
     States cannot be applied to a programme that has roughly quadrupled in a decade: the
     multiplier encodes accumulated history the Indian programme has not had yet.

     60,000 is derived as the last fifteen years of activity, about 96,000 transplants, at a
     retention of roughly 60 per cent once graft failure, patient mortality and loss to
     follow-up are allowed for. It is an estimate and is graded as one. It is not precise, but
     it is the right order of magnitude, which 141,151 was not.
     --------------------------------------------------------------------------------- */
  IND: {
    value: 60_000, organ: 'kidney', asAt: '2023-12-31',
    source: 'DERIVED, not published. Roughly 96,000 kidney transplants over the fifteen years '
          + 'to 2023 (NOTTO series) at about 60 per cent retention. India publishes no '
          + 'functioning-graft count. Treat as an order of magnitude.',
  },
};

/* The bounds the four anchors actually describe. Used to state a range for the countries with
   no published figure, because a single number there would imply a precision that does not
   exist. The low bound is the United States, the high bound Australia. */
export const MULTIPLIER_RANGE = { low: 9.75, high: 13.65 } as const;

/* The default for a country with no published count. The LOW bound deliberately: most of the
   unanchored volume sits in younger, faster-growing programmes where the true multiplier is
   below every anchor, so the conservative end is the honest default and the range is shown
   alongside it. */
export const DEFAULT_KIDNEY_MULTIPLIER = MULTIPLIER_RANGE.low;
